import {
	Response,
	BillingAddress,
	BillingBusiness,
	Credit,
	UserSubscriptionDetails,
	UserTransaction,
	UserAdjustment,
} from "@version2/domain-package";
import type { MessageInstance } from "@version2/ui-package";
import { apiGet, apiPost, apiPatch } from "./client";

export const ensurePaddleCustomer = async (): Promise<string> => {
	const response = await apiPost("/v1/billing/customer/ensure", {});
	const customerId = response.data?.paddle_user_id;
	if (!response.success || typeof customerId !== "string" || !customerId) {
		throw response;
	}
	return customerId;
};

/**
 * Get the customer's credit balance
 */
export const getCreditBalance = async (message: MessageInstance): Promise<Credit[]> => {
	try {
		const response = await apiGet("/v1/billing/credit-balance");
		if (!response.success || !response.data) {
			throw response;
		}
		return (response.data.credits || []).map(Credit.fromJson);
	} catch (error) {
		message.error("Error getting billing credit, please try again later.");
		console.error(error);
		throw error;
	}
};

/**
 * Get subscription details
 */
export const getSubscriptionDetails = async (
	message: MessageInstance,
	internalSubscriptionId: string
): Promise<UserSubscriptionDetails> => {
	try {
		const response = await apiGet(`/v1/billing/subscriptions/${internalSubscriptionId}`);
		if (!response.success || !response.data) {
			throw response;
		}
		return UserSubscriptionDetails.fromJson(response.data);
	} catch (error) {
		message.error("Error getting subscription details, please try again later.");
		console.error(error);
		throw error;
	}
};

/**
 * Cancel a subscription
 */
export const cancelSubscription = async (
	message: MessageInstance,
	internalSubscriptionId: string
): Promise<unknown> => {
	try {
		const response = await apiPost(
			`/v1/billing/subscriptions/${internalSubscriptionId}/cancel`,
			{}
		);
		if (!response.success) {
			throw response;
		}
		return response.data;
	} catch (error) {
		message.error("Error cancelling subscription, please try again later.");
		console.error(error);
		throw error;
	}
};

/**
 * Get transaction details for payment method update
 */
export const getTransactionUpdateDetails = async (
	message: MessageInstance,
	internalSubscriptionId: string
): Promise<{ transaction_id: string | number }> => {
	try {
		const response = await apiGet(
			`/v1/billing/subscriptions/${internalSubscriptionId}/payment-method-transaction`
		);
		if (!response.success || !response.data) {
			throw response;
		}
		return response.data as { transaction_id: string | number };
	} catch (error) {
		message.error("Error getting payment method details, please try again later.");
		console.error(error);
		throw error;
	}
};

/**
 * Get invoice for a transaction
 */
export const getTransactionInvoice = async (
	message: MessageInstance,
	transactionId: string
): Promise<string | undefined> => {
	try {
		const response = await apiGet(`/v1/billing/transactions/${transactionId}/invoice`);
		if (!response.success) {
			throw response;
		}
		return response.data?.url;
	} catch (error) {
		message.error("Error getting subscription details, please try again later.");
		console.error(error);
		throw error;
	}
};

export const getSubscriptionTransactions = async (
	message: MessageInstance,
	internalSubscriptionId: string
): Promise<UserTransaction[]> => {
	try {
		const response = await apiGet(
			`/v1/billing/subscriptions/${internalSubscriptionId}/transactions`
		);
		if (!response.success) {
			throw response;
		}
		return (
			response.data?.transactions?.map((transaction: any) =>
				UserTransaction.fromJson(transaction)
			) || []
		);
	} catch (error) {
		message.error("Error getting subscription transactions, please try again later.");
		console.error(error);
		throw error;
	}
};

export const getSubscriptionAdjustments = async (
	message: MessageInstance,
	internalSubscriptionId: string
): Promise<UserAdjustment[]> => {
	try {
		const response = await apiGet(
			`/v1/billing/subscriptions/${internalSubscriptionId}/adjustments`
		);
		if (!response.success) {
			throw response;
		}
		return (
			response.data?.adjustments?.map((adjustment: any) =>
				UserAdjustment.fromJson(adjustment)
			) || []
		);
	} catch (error) {
		message.error("Error getting subscription adjustments, please try again later.");
		console.error(error);
		throw error;
	}
};

/**
 * Update billing address
 */
export const updateBillingAddress = async (
	message: MessageInstance,
	address: BillingAddress
): Promise<Response> => {
	try {
		const response = await apiPatch("/v1/billing/address", {
			address: address.toJson(),
		});
		if (!response.success) {
			throw response;
		}
		message.success("Billing address updated successfully.");
		return response;
	} catch (error) {
		message.error("Error updating billing address, please verify the address is correct.");
		console.error(error);
		throw error;
	}
};

/**
 * Update billing business details
 */
export const updateBillingBusiness = async (
	message: MessageInstance,
	business: BillingBusiness,
	internalSubscriptionId: string
): Promise<Response> => {
	try {
		const response = await apiPatch("/v1/billing/business", {
			business: business.toJson(),
			internal_subscription_id: internalSubscriptionId,
		});
		if (!response.success) {
			throw response;
		}
		message.success("Business details updated successfully.");
		return response;
	} catch (error) {
		message.error("Error updating business details, please verify the business is correct.");
		console.error(error);
		throw error;
	}
};
